import { useState, useEffect, useCallback } from 'react';
import { useUserSession } from '../../context/UserSessionContext';
import TwilioAPI from '../api/twilioAPI';
import firestoreService from './templatesFirestoreService';

export default function useTemplatesHook() {
  const { effectiveUserId } = useUserSession();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to real-time template updates
  useEffect(() => {
    if (!effectiveUserId) return;

    setLoading(true);
    const unsubscribe = firestoreService.subscribeToUserTemplates(
      effectiveUserId,
      (updatedTemplates) => {
        setTemplates(updatedTemplates);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId]);

  // Sync approval statuses with Twilio periodically
  const syncApprovalStatuses = useCallback(async () => {
    if (!effectiveUserId) return;

    try {
      const pendingTemplates = templates.filter(t => t.status === 'pending' && t.twilioContentSid);
      
      for (const template of pendingTemplates) {
        try {
          const approvalStatus = await TwilioAPI.getApprovalStatus(template.twilioContentSid);
          
          if (approvalStatus && approvalStatus.status !== 'pending') {
            let newStatus = 'pending';
            
            switch (approvalStatus.status) {
              case 'approved':
                newStatus = 'approved';
                break;
              case 'rejected':
                newStatus = 'declined';
                break;
              case 'paused':
                newStatus = 'paused';
                break;
              case 'disabled':
                newStatus = 'disabled';
                break;
            }
            
            await firestoreService.updateTemplateStatus(
              effectiveUserId,
              template.machineName,
              newStatus,
              approvalStatus.rejectionReason
            );
          }
        } catch (err) {
          console.error(`Failed to sync status for template ${template.machineName}:`, err);
        }
      }
    } catch (error) {
      console.error('Failed to sync approval statuses:', error);
    }
  }, [effectiveUserId, templates]);

  // Sync statuses every 5 minutes for pending templates
  useEffect(() => {
    if (templates.some(t => t.status === 'pending')) {
      const interval = setInterval(syncApprovalStatuses, 5 * 60 * 1000); // 2 minutes
      return () => clearInterval(interval);
    }
  }, [templates, syncApprovalStatuses]);

  const createTemplate = async (templateData) => {
    if (!effectiveUserId) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);

      // Don't overwrite the variables - keep them as provided by the template creator
      // templateData.variables is already set correctly from TemplateManager

      // 1. Create template in Twilio
      const twilioTemplate = await TwilioAPI.createTemplate(templateData);

      // 2. Submit for WhatsApp approval
      const approvalRequest = await TwilioAPI.submitForWhatsAppApproval(
        twilioTemplate.sid,
        templateData.machineName,
        templateData.category || 'MARKETING'
      );
      
      // 3. Store in Firestore
      const firestoreTemplate = await firestoreService.createTemplate(effectiveUserId, {
        ...templateData,
        twilioContentSid: twilioTemplate.sid,
        twilioApprovalSid: approvalRequest.sid
      });
      
      return firestoreTemplate;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const getTemplate = async (templateName) => {
    if (!effectiveUserId) return null;
    
    try {
      return await firestoreService.getTemplate(effectiveUserId, templateName);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updateTemplate = async (templateName, updates) => {
    if (!effectiveUserId) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      await firestoreService.updateTemplate(effectiveUserId, templateName, updates);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const deleteTemplate = async (templateName) => {
    if (!effectiveUserId) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      await firestoreService.deleteTemplate(effectiveUserId, templateName);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const refreshApprovalStatus = async (templateName) => {
    if (!effectiveUserId) return;

    try {
      const template = await firestoreService.getTemplate(effectiveUserId, templateName);
      if (!template?.twilioContentSid) return;

      const approvalStatus = await TwilioAPI.getApprovalStatus(template.twilioContentSid);
      
      if (approvalStatus) {
        let newStatus = 'pending';
        
        switch (approvalStatus.status) {
          case 'approved':
            newStatus = 'approved';
            break;
          case 'rejected':
            newStatus = 'declined';
            break;
          case 'paused':
            newStatus = 'paused';
            break;
          case 'disabled':
            newStatus = 'disabled';
            break;
        }
        
        await firestoreService.updateTemplateStatus(
          effectiveUserId,
          templateName,
          newStatus,
          approvalStatus.rejectionReason
        );
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  return {
    templates,
    loading,
    error,
    createTemplate,
    getTemplate,
    updateTemplate,
    deleteTemplate,
    refreshApprovalStatus,
    syncApprovalStatuses
  };
}